import React from "react";
import { useCurrency } from "../../utils/Currencies";

import { useTranslation, Trans } from "react-i18next";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";

import Typography from "@mui/material/Typography";
import DialogTitleWithCloseButton from "../DialogTitleWithCloseButton";

import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

import Container from "@mui/material/Container";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import { useCart } from "../../utils/CartHooks";

/**
 * Pricing Tab Panel
 * The text will depend on the props
 *
 */
function PricingTabPanel(props) {
  const {
    tabData,
    selectedIndex,
    index,
    designId,
    designFromDesignState,
    sizesOption,
    ...other
  } = props;

  /**
   * Add to cart and redirect to cart
   */
  //const history = useHistory();
  //const { dispatch } = useStore();
  const { addToCart } = useCart();
  const onAddToCart = async (currency) => {
    addToCart(
      designId,
      sizesOption,
      currency,
      designFromDesignState.product.name
    );
  };

  const useStyles = makeStyles({
    table: {},
    rowcell: {
      borderBottom: "none",
    },
    footercell: {
      fontSize: "1.0rem",
      fontWeight: "bold",
      color: "black",
    },
    list: {
      listStyleType: "disc",
    },
    dialogActions: {
      justifyContent: "center",
      backgroundColor: "#20497A",
      color: "white",
      marginTop: "10px",
      marginBottom: "10px",
    },
    bottomText: {
      fontStyle: "italic",
      textAlign: "left",
    },
    addToCartButton: {
      width: "100%",
    },
    creditsBox: {
      minHeight: "25vh",
      maxHeight: "25vh",
      overflow: "auto",
      paddingTop: "16px",
      paddingBottom: "16px",
    },
    currencyLink: {
      cursor: "pointer",
      color: "#3e6bad",
      fontSize: "0.75rem",
      textDecoration: "underline",
    },
  });

  const { t } = useTranslation();

  const classes = useStyles();

  const {
    FullCurrencyFormatter,
    NarrowCurrencyFormatter,
    getCurrencySymbol,
    isCAD,
    isUSD,
    setUSD,
    setCAD,
    getCurrency,
  } = useCurrency();

  const setUSDCurrency = () => {
    setUSD();
  };

  const setCADCurrency = () => {
    setCAD();
  };

  return (
    <TabPanel selectedIndex={selectedIndex} index={index} {...other}>
      <TableContainer component={Container}>
        <Table className={classes.table} aria-label="simple table">
          <TableBody>
            <TableRow key="item">
              <TableCell className={classes.rowcell} align="left">
                {tabData.name}
              </TableCell>
              <TableCell className={classes.rowcell} align="right">
                {NarrowCurrencyFormatter.format(tabData.unit_price)}
              </TableCell>
            </TableRow>

            <TableRow key="customization">
              <TableCell className={classes.rowcell} align="left">
                {t("pricing.customization")}
              </TableCell>
              <TableCell className={classes.rowcell} align="right">
                {NarrowCurrencyFormatter.format(
                  tabData.customizationTotalPrice
                )}
              </TableCell>
            </TableRow>
          </TableBody>
          <TableFooter>
            <TableRow key="total">
              <TableCell align="left" className={classes.footercell}>
                <span>{t("pricing.total")}</span>
                <span>{" (" + getCurrencySymbol() + ") "}</span>
                {isUSD() && (
                  <span
                    className={classes.currencyLink}
                    onClick={setCADCurrency}
                  >
                    {t("pricing.change_currency_cad")}
                  </span>
                )}
                {isCAD() && (
                  <span
                    className={classes.currencyLink}
                    onClick={setUSDCurrency}
                  >
                    {t("pricing.change_currency_usd")}
                  </span>
                )}
              </TableCell>
              <TableCell align="right" className={classes.footercell}>
                {NarrowCurrencyFormatter.format(tabData.total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <Box border={1} p={4} className={classes.creditsBox}>
        <Typography paragraph>
          {tabData.freeCreditsOfDesign > 100 ? (
            <Trans i18nKey="pricing.credits_unlimited"></Trans>
          ) : (
            <Trans i18nKey="pricing.credits_general_info">
              {/*TODO: make this icon a real span with role img with aria-label*/}
              This item comes with ✨{" "}
              {{ creditsFreeCount: tabData.freeCreditsOfDesign }} free credits.
              Extra credits are{" "}
              {{
                creditPrice: FullCurrencyFormatter.format(
                  tabData.singleCreditPrice
                ),
              }}{" "}
              each.{" "}
              <a
                href="https://www.jameo.com/faq"
                target="_blank"
                rel="noopener noreferrer"
              >
                credits list
              </a>
              .
            </Trans>
          )}
        </Typography>
        <Typography paragraph>
          {t("pricing.credits_used_design", {
            creditsUsed: tabData.creditsUsedInDesign,
          })}
          {/*TODO Make the translation plurial and singular check https://react.i18next.com/latest/trans-component#using-with-react-components*/}
        </Typography>

        <ul className={classes.list}>
          {tabData.customizationsListWithCredits.map((item) => (
            <li key={`item-${item.id}`}>
              {t("pricing.credits_used_feature", {
                customizationName: item.displayLabels
                  .map((d, i) => {
                    //Bad hack here, aligned with specs pricing displayLabels for decorations, but it's because the name of a decoration can be changed by a user and shoudn't be translated
                    if (item.type === "ARTWORK") {
                      if (i === 2) {
                        return t(`positions.${d}`);
                      } else if (i > 0) {
                        return `${d}`;
                      } else {
                        return t(`specs.decoration_types.${d}.title`);
                      }
                    } else {
                      if (item.type === "First color") {
                        return (
                          t("pricing.first_color") +
                          " (" +
                          t(`colors.${d}`) +
                          ") " +
                          t("pricing.in_fabric") +
                          " " +
                          t(`fabric.${item.fabric}`)
                        );
                      } else if (item.type === "Extra color") {
                        return (
                          t("pricing.extra_color") +
                          " (" +
                          t(`colors.${d}`) +
                          ") " +
                          t("pricing.in_fabric") +
                          " " +
                          t(`fabric.${item.fabric}`)
                        );
                      }
                    }
                    return t(
                      `specs.${designFromDesignState.product.name}.${d}`
                    );
                  })
                  .join(" - "),
                creditsCount: item.credits,
              })}
            </li>
          ))}
        </ul>
      </Box>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={() => onAddToCart(getCurrency())}
          color="inherit"
          className={classes.addToCartButton}
        >
          {t("pricing.add_to_cart")}
        </Button>
      </DialogActions>

      <Typography paragraph className={classes.bottomText}>
        {t("pricing.change_later")}
      </Typography>
    </TabPanel>
  );
}

/** 
  Each individual panel will be a box with it's children in it
  And will be visible only if it's "value" is the same as the current tab index selected
*/
function TabPanel(props) {
  const { children, selectedIndex, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={selectedIndex !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      p={3}
      {...other}
    >
      {index === selectedIndex && <React.Fragment>{children}</React.Fragment>}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  selectedIndex: PropTypes.any.isRequired,
};

/**
 * Tabs bar full width
 *
 */
const FullWidthTabs = (props) => {
  const {
    designFromDesignState,
    designPricing,
    pricingDef,
    sizesOption,
    designId,
  } = props;

  //This is the selected tab index value
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const handleChange = (event, newSelectedIndex) => {
    setSelectedIndex(newSelectedIndex);
  };

  const { t } = useTranslation();

  function a11yPropsTabButton(index) {
    return {
      id: `full-width-tab-${index}`,
      "aria-controls": `full-width-tabpanel-${index}`,
    };
  }

  const useStyles = makeStyles((theme) => ({
    tab: {
      "&:focus": {
        outline: "0px", //Super important here or else we have weird onFocus behaviour (outline is blue instead of no outlines)
      },
      minWidth: "50px",
      fontSize: "1.0rem",
      textTransform: "none",
    },
    tabs: {
      flexGrow: 1,
      color: "black",
      paddingLeft: "32px",
      paddingRight: "32px",
    },
    selected: {},
  }));

  const classes = useStyles();

  const { getCurrency } = useCurrency();
  //Create the price information from the design object
  //First need to know how many tabs and the range of each tabs
  let tabsData = [];
  let minQty = pricingDef.minQty;
  let lastQtyKey = minQty;
  let currency = getCurrency();
  const pricingCurrency = pricingDef.pricingCurrency[currency];

  const extraCredits =
    designPricing.creditsUsedInDesign - pricingDef.freeCredits > 0
      ? designPricing.creditsUsedInDesign - pricingDef.freeCredits
      : 0;

  for (const range of pricingCurrency.pricingRange) {
    tabsData.push({
      key: range.qty,
      lbound: lastQtyKey,
      ubound: range.qty - 1,
      price: range.price,
      name: t("products." + designFromDesignState.product.name + ".name"),
      unit_price: range.price,
      shipping_price: range.shipping,
      total: pricingCurrency.priceOfExtraCredit * extraCredits + range.price,
      customizationsListWithCredits:
        designPricing.customizationsListWithCredits,
      customizationTotalPrice:
        pricingCurrency.priceOfExtraCredit * extraCredits,
      singleCreditPrice: pricingCurrency.priceOfExtraCredit,
      freeCreditsOfDesign: pricingDef.freeCredits,
      creditsUsedInDesign: designPricing.creditsUsedInDesign,
    });

    lastQtyKey = range.qty;
  }

  return (
    <React.Fragment>
      <Tabs
        className={classes.tabs}
        value={selectedIndex}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="inherit"
        aria-label="full width tabs example"
        variant="fullWidth"
      >
        {/*Loop pricing info and create dynamic tab with pricing data and credits info*/}
        {tabsData.map((tabData) => (
          <Tab
            key={tabData.key}
            className={classes.tab}
            label={t("pricing.price_range", {
              lbound: tabData.lbound,
              ubound: tabData.ubound,
            })}
            {...a11yPropsTabButton(0)}
          />
        ))}
        <Tab
          key={lastQtyKey}
          className={classes.tab}
          label={t("pricing.price_range_plus", {
            qty_max: lastQtyKey,
          })}
          {...a11yPropsTabButton(0)}
        />
      </Tabs>

      {/*Loop pricing info and create dynamic tab with pricing data and credits info*/}
      {tabsData.map((tabData, index) => (
        <PricingTabPanel
          key={index}
          selectedIndex={selectedIndex}
          index={index}
          value={index}
          tabData={tabData}
          designId={designId}
          designFromDesignState={designFromDesignState}
          sizesOption={sizesOption}
        />
      ))}
      {/*Last range is special and customer need to contact us */}
      <TabPanel selectedIndex={selectedIndex} index={tabsData.length}>
        <Typography paragraph>{t("pricing.contactus")}</Typography>
        <Typography paragraph>
          {/* Needed the Trans component to translate and keep the a href*/}
          <Trans i18nKey="pricing.goingbig">
            Contact <a href="mailto:info@jameo.com">info@jameo.com</a> for a
            tailored price and a dedicated Account Manager will be in touch
            soon.
          </Trans>
        </Typography>
      </TabPanel>
    </React.Fragment>
  );
};

const CalculatePriceModal = (props) => {
  const {
    open,
    onClose,
    designId,
    designFromDesignState,
    designPricing,
    pricingDef,
    sizesOption,
    userInfo,
  } = props;

  const handleClickClose = () => {
    onClose();
  };

  const useStyles = makeStyles((theme) => ({
    dialogPaper: {
      minHeight: "90vh",
      maxHeight: "90vh",
      minWidth: "700px",
      paddingTop: "32px",
      paddingBottom: "32px",
    },
  }));

  const classes = useStyles();

  const { t } = useTranslation();

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper }}
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitleWithCloseButton
        id="customized-dialog-title"
        onClose={handleClickClose}
      >
        {t("pricing.calculate_price")}
      </DialogTitleWithCloseButton>

      <DialogContent>
        {designFromDesignState && designPricing && (
          <FullWidthTabs
            designId={designId}
            designFromDesignState={designFromDesignState}
            sizesOption={sizesOption}
            designPricing={designPricing}
            pricingDef={pricingDef}
            userInfo={userInfo}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Design object contains
 */
CalculatePriceModal.propTypes = {
  designId: PropTypes.string.isRequired,
  designFromDesignState: PropTypes.any.isRequired,
  designPricing: PropTypes.any.isRequired,
  sizesOption: PropTypes.any.isRequired,
};

export default CalculatePriceModal;
